import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useRentalManagerUserSingleFamilyEnterpriseContractWebApiHandler_contracts } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';
import DispatchConstants from '../constants/DispatchConstants';
import ErrorMessageConstants from '../constants/ErrorMessageConstants';
import SingleFamilyContractDetails from '../model/SingleFamilyContractDetails';
import { UserSelectors } from '../selectors';

import type { Dispatch } from 'react';
import type { ListingErrorsReducerProps, ListingReducerProps } from '../reducers/listing';

const useContracts = (
  setListing: Dispatch<{ type: keyof ListingReducerProps; payload: unknown }>,
  setErrorObj: Dispatch<ListingErrorsReducerProps>,
  setIsSubmitDisabled: (value: boolean) => void,
) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => UserSelectors.makeUser(state));
  const shouldGetSFContracts = user.isSingleFamilyEnterpriseCustomer && user.userManagementCompanyId;

  const { refetch: refetchContracts } = useRentalManagerUserSingleFamilyEnterpriseContractWebApiHandler_contracts({
    enabled: false,
  });

  useEffect(() => {
    if (!shouldGetSFContracts) return;

    const fetchContracts = async () => {
      setIsSubmitDisabled(true);
      try {
        const { data: contractsData } = await refetchContracts();

        if (contractsData) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const contractsModel = contractsData.contracts.map((c) => SingleFamilyContractDetails(c as any));
          dispatch({
            type: DispatchConstants.SINGLE_FAMILY_SET_CONTRACTS,
            payload: {
              contracts: contractsModel,
              hasAnyExpiredContract: contractsData.hasAnyExpiredContract || false,
            },
          });

          setIsSubmitDisabled(false);

          if (contractsModel.length === 1) {
            setListing({
              type: 'customerId',
              payload: contractsModel[0].customerId,
            });
          }
        }
      } catch {
        setIsSubmitDisabled(true);
        setErrorObj({ type: 'customerId', payload: ErrorMessageConstants.SINGLE_FAMILY_CANNOT_RETRIEVE_CONTRACTS });
      }
    };

    fetchContracts();
  }, [user, shouldGetSFContracts, refetchContracts]);
};

export default useContracts;
